import { ReferenceTable } from "@/components/ReferenceTable";
import { REF_VIMSHOTTARI_EN } from "@/lib/reference-tables.generated";

// Engine-anchored Vimshottari antardasha depth. Every duration is a FIXED
// classical constant computed directly from the engine formula
//   ad_years = mahadasha_years × DASHA_YEARS[sub_lord] / 120
// (business/ishvaram/apps/backend/app/engine/calculators/dasha.py::_calculate_antardasha
//  + constants.py DASHA_YEARS / TOTAL_DASHA_YEARS=120). Sub-lord order = the
// standard Vimshottari cycle (DASHA_SEQUENCE in ./_data.ts). NOT per-user
// computed (rules 24/29). Semantic design tokens only.

// Each sub-lord's share of ANY mahadasha = its dasha-years ÷ 120.
const SHARE_ROWS: ReadonlyArray<readonly [string, string, string]> = [
  ["Ketu", "7", "5.83%"],
  ["Venus", "20", "16.67%"],
  ["Sun", "6", "5.00%"],
  ["Moon", "10", "8.33%"],
  ["Mars", "7", "5.83%"],
  ["Rahu", "18", "15.00%"],
  ["Jupiter", "16", "13.33%"],
  ["Saturn", "19", "15.83%"],
  ["Mercury", "17", "14.17%"],
];

// Worked example: the 20-year Venus mahadasha, in sequence from the mahadasha
// lord. Durations = 20 × DASHA_YEARS[sub] / 120, rounded to whole months.
const VENUS_EXAMPLE_ROWS: ReadonlyArray<readonly [string, string]> = [
  ["Venus – Venus", "3 years 4 months"],
  ["Venus – Sun", "1 year 0 months"],
  ["Venus – Moon", "1 year 8 months"],
  ["Venus – Mars", "1 year 2 months"],
  ["Venus – Rahu", "3 years 0 months"],
  ["Venus – Jupiter", "2 years 8 months"],
  ["Venus – Saturn", "3 years 2 months"],
  ["Venus – Mercury", "2 years 10 months"],
  ["Venus – Ketu", "1 year 2 months"],
];

export function AntardashaDepth() {
  return (
    <>
      <ReferenceTable
        dataset={REF_VIMSHOTTARI_EN}
        heading="Vimshottari Mahadasha — years each graha rules"
        intro="The Vimshottari cycle assigns every graha a fixed number of years; the nine periods total 120 years."
        minWidthClass="min-w-[320px]"
      />
      <h2 className="mb-4 mt-10 font-serif text-xl font-bold text-ish-dark">
        How Long Each Antardasha Lasts — the Sub-Period Table
      </h2>
      <p className="mb-4 text-body leading-relaxed">
        Every Mahadasha is divided into exactly nine Antardashas (Bhukti sub-periods), and their
        lengths are not equal — each sub-period&apos;s share is fixed by the same Vimshottari
        proportions that set the Mahadasha lengths. The rule is deterministic: an Antardasha runs
        for (Mahadasha years × that sub-lord&apos;s own dasha years) ÷ 120. So the longest cycle
        (Venus, 20 years) always owns the biggest slice of any Mahadasha, and the shortest (Sun, 6
        years) the smallest. The first Antardasha in every Mahadasha belongs to the Mahadasha lord
        itself, after which the sub-periods follow the standard planetary order.
      </p>
      <div className="mb-6 overflow-x-auto rounded-lg border border-ish-border bg-ish-bg-card">
        <table className="min-w-[480px] w-full border-collapse text-left text-caption">
          <thead className="bg-ish-accent-light text-ish-dark">
            <tr>
              <th className="px-3 py-2 font-semibold">Sub-lord</th>
              <th className="px-3 py-2 font-semibold">Own dasha years</th>
              <th className="px-3 py-2 font-semibold">Share of any Mahadasha</th>
            </tr>
          </thead>
          <tbody>
            {SHARE_ROWS.map(([lord, years, share]) => (
              <tr key={lord} className="border-t border-ish-border">
                <td className="px-3 py-2 font-semibold text-ish-dark align-top">{lord}</td>
                <td className="px-3 py-2 align-top">{years}</td>
                <td className="px-3 py-2 align-top">{share}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mb-4 text-body leading-relaxed">
        Worked example — the 20-year Venus Mahadasha, in sequence from the Mahadasha lord (the nine
        rows sum to the full 20 years):
      </p>
      <div className="mb-6 overflow-x-auto rounded-lg border border-ish-border bg-ish-bg-card">
        <table className="min-w-[420px] w-full border-collapse text-left text-caption">
          <thead className="bg-ish-accent-light text-ish-dark">
            <tr>
              <th className="px-3 py-2 font-semibold">Antardasha</th>
              <th className="px-3 py-2 font-semibold">Duration</th>
            </tr>
          </thead>
          <tbody>
            {VENUS_EXAMPLE_ROWS.map(([pair, dur]) => (
              <tr key={pair} className="border-t border-ish-border">
                <td className="px-3 py-2 font-semibold text-ish-dark align-top">{pair}</td>
                <td className="px-3 py-2 align-top">{dur}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mb-4 mt-10 font-serif text-xl font-bold text-ish-dark">
        How a Mahadasha–Antardasha Pair Is Actually Read
      </h2>
      <p className="mb-4 text-body leading-relaxed">
        Knowing which two lords are running is only the start; the reading comes from four evergreen
        questions a Jyotishi asks of the PAIR, not the planet&apos;s generic reputation. First, what
        does each lord promise in the birth chart — which houses it owns from the lagna and which
        house it sits in — because a planet delivers in its dasha whatever it committed to in the
        kundali, so the same Venus Antardasha uplifts one chart and strains another purely from its
        natal placement. Second, how strong and how afflicted is each lord (sign dignity, aspects on
        it, whether it is retrograde or combust). Third, how the two lords relate to each other —
        friends, neutrals or enemies — which sets whether the sub-period cooperates with the
        Mahadasha theme or pulls against it. Fourth, what the current transits are doing to those
        two lords, which is what turns a standing promise into a dated event.
      </p>

      <h2 className="mb-4 mt-10 font-serif text-xl font-bold text-ish-dark">
        Dasha Sandhi — Reading the Junction Between Two Periods
      </h2>
      <p className="mb-4 text-body leading-relaxed">
        The transition point where one Mahadasha ends and the next begins is called dasha sandhi,
        and classical texts treat it as a distinct reading rather than a clean switch. At a sandhi
        the life theme itself is changing hands — the outgoing lord is settling its final accounts
        while the incoming lord has not yet fully taken charge — so the period can feel unsettled or
        transitional even when neither planet is difficult. The tradition&apos;s guidance is
        practical, not fearful: it counsels extra reflection around big irreversible decisions at a
        Mahadasha junction, because the ground under the current life theme is shifting. The same
        sandhi logic applies in miniature at every Antardasha and Pratyantardasha boundary.
      </p>
    </>
  );
}
